import { Icon, Style as olStyle } from 'ol/style';
import olVectorLayer from 'ol/layer/Vector';
import olVectorSource from 'ol/source/Vector';
import olFeature from 'ol/Feature';
import BaseLayer from 'ol/layer/Base';
import olFillStyle from 'ol/style/Fill';
import olStrokeStyle from 'ol/style/Stroke';
import olCircleStyle from 'ol/style/Circle';
import image from '@assets/images/map-controls/place.svg';

export interface MarkerOptions {
  removeLayer(layer: BaseLayer): BaseLayer | undefined;
  addLayer(layer: BaseLayer): void;
  zIndex?: number;
}

export interface IMarker<Geom> {
  create(geom: Geom, style?: StyleVariant): void;
  remove(): void;
}

const STYLE_HASH: Record<StyleVariant, olStyle> = {
  outlined: new olStyle({
    stroke: new olStrokeStyle({
      width: 4,
      color: [51, 153, 204, 1],
    }),
    image: new Icon({
      anchor: [0.5, 40],
      anchorXUnits: 'fraction',
      anchorYUnits: 'pixels',
      src: image,
    }),
  }),
  filled: new olStyle({
    fill: new olFillStyle({
      color: [255, 255, 0, 0.2],
    }),
    stroke: new olStrokeStyle({
      color: [255, 255, 0, 1],
    }),
    image: new olCircleStyle({
      radius: 5,
      fill: new olFillStyle({
        color: [255, 255, 0, 0.2],
      }),
      stroke: new olStrokeStyle({
        color: [255, 255, 0, 1],
      }),
    }),
  }),
};

export type StyleVariant = 'outlined' | 'filled';

export class MapMarker implements IMarker<olFeature[]> {
  private _zIndex: number;
  private _layer?: olVectorLayer;
  private _source?: olVectorSource;
  private _style?: olStyle;
  private _removeLayer: (layer: BaseLayer) => BaseLayer | undefined;
  private _addLayer: (layer: BaseLayer) => void;

  constructor({ addLayer, removeLayer, zIndex = Infinity }: MarkerOptions) {
    this._addLayer = addLayer;
    this._removeLayer = removeLayer;
    this._zIndex = zIndex;
  }

  create = (features: olFeature[], style?: StyleVariant) => {
    if (this._source) {
      this._source.clear();
      this._source.addFeatures(features);
    } else {
      this._layer = this._createLayer(features, style);
      this._addLayer(this._layer);
    }
  };

  remove = () => {
    if (!this._layer) return;

    this._removeLayer(this._layer);
    this._layer.dispose();

    if (!this._source) return;

    this._source.dispose();
    this._source = undefined;
  };

  private _createLayer = (
    features: olFeature[],
    style: StyleVariant = 'filled',
  ): olVectorLayer => {
    this._source = new olVectorSource({ features });
    this._style = STYLE_HASH[style];

    return new olVectorLayer({
      source: this._source,
      zIndex: this._zIndex,
      style: this._style,
    });
  };
}
